import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { api, ApiError, download, fetchBlobUrl, request } from "../api";
import { Table } from "../components/Table";
import type { Column } from "../components/Table";
import { Modal } from "../components/Modal";
import { MoneyInput } from "../components/MoneyInput";
import { DateInput } from "../components/DateInput";
import { fmtDate, fmtINR } from "../fmt";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const NON_SALARY_KINDS = ["rent", "interest", "dividend", "other"] as const;

// Item-wise group display names + order (mirrors the API's GROUP_ORDER).
const GROUP_LABELS: Record<string, string> = {
  Earning: "Earnings",
  LoanReceived: "Loans received",
  Deduction: "Deductions",
  Perk: "Perks",
  Memo: "Memo",
};

interface Income {
  id: string;
  kind: "salary" | "rent" | "interest" | "dividend" | "other";
  periodMonth: string;
  employerOrSource: string | null;
  gross: number | null;
  basic: number | null;
  net: number | null;
  notes: string | null;
}

interface SalaryCell {
  employer: string;
  gross: number | null;
  basic: number | null;
  net: number | null;
  saved: boolean;
}

interface FyRow {
  fy: string;
  fyStart: number;
  gross: number;
  basic: number;
  net: number;
  months: number;
}
interface Analytics {
  fys: FyRow[];
  totals: { gross: number; basic: number; net: number; months: number };
  minFy: string | null;
  maxFy: string | null;
  count: number;
}

interface YtdItem {
  code: string;
  description: string;
  group: string;
  subCategory: string | null;
  total: number;
}
interface YtdGroup {
  group: string;
  total: number;
  items: YtdItem[];
}
interface PayrollYtd {
  grandTotals: {
    grossEarnings: number;
    netCredited: number;
    totalDeductions: number;
    totalPerks: number;
    totalLoansReceived: number;
    itemCount: number;
  };
  groups: YtdGroup[];
  count: number;
  caption: string;
}

interface ParsedUpload {
  periodMonth: string | null;
  gross: number | null;
  basic: number | null;
  net: number | null;
  documentId: string;
  matched: boolean;
  confirmed: boolean;
}

interface DocRow {
  id: string;
  title: string;
}

function currentFy(): number {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
}

function fyMonths(fyStartYear: number): { key: string; label: string }[] {
  const out: { key: string; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const monthIndex = (3 + i) % 12;
    const year = monthIndex >= 3 ? fyStartYear : fyStartYear + 1;
    const mm = String(monthIndex + 1).padStart(2, "0");
    out.push({ key: `${year}-${mm}-01`, label: `${MONTH_NAMES[monthIndex]} ${year}` });
  }
  return out;
}

/** Short FY axis label, e.g. "FY2024-25" -> "24-25". */
function shortFy(fy: string): string {
  const m = /FY(\d{2})(\d{2})-(\d{2})/.exec(fy);
  return m ? `${m[2]}-${m[3]}` : fy;
}

type Tab = "year" | "item" | "monthly";

export function Incomes() {
  const [tab, setTab] = useState<Tab>("year");
  const [error, setError] = useState<string | null>(null);

  return (
    <div>
      <h1>Salary &amp; Income</h1>
      {error && <div className="error-text">{error}</div>}

      <div className="toolbar" role="tablist">
        <button type="button" className={tab === "year" ? "primary" : ""} onClick={() => setTab("year")}>
          Year-wise
        </button>
        <button type="button" className={tab === "item" ? "primary" : ""} onClick={() => setTab("item")}>
          Item-wise (lifetime)
        </button>
        <button type="button" className={tab === "monthly" ? "primary" : ""} onClick={() => setTab("monthly")}>
          Monthly &amp; other income
        </button>
      </div>

      {tab === "year" && <YearWise setError={setError} onDrillFy={() => setTab("monthly")} />}
      {tab === "item" && <ItemWise setError={setError} />}
      {tab === "monthly" && <MonthlyAndOther setError={setError} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Year-wise: FY table + inline-SVG bar chart (gross by FY) + net-vs-gross
// trend, FY-range filter, totals row, xlsx export.
// ---------------------------------------------------------------------------
function YearWise({ setError, onDrillFy }: { setError: (e: string | null) => void; onDrillFy: (fy: number) => void }) {
  const [data, setData] = useState<Analytics | null>(null);
  const [fromFy, setFromFy] = useState("");
  const [toFy, setToFy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (fromFy) qs.set("fromFy", fromFy);
    if (toFy) qs.set("toFy", toFy);
    const suffix = qs.toString() ? `?${qs}` : "";
    api
      .get<Analytics>(`/api/incomes/analytics${suffix}`)
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load analytics"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [fromFy, toFy]);

  // Full (unfiltered) FY list for the range dropdowns.
  const [allFys, setAllFys] = useState<FyRow[]>([]);
  useEffect(() => {
    api
      .get<Analytics>("/api/incomes/analytics")
      .then((d) => setAllFys(d.fys))
      .catch(() => setAllFys([]));
  }, []);

  const onExport = () => {
    const qs = new URLSearchParams();
    if (fromFy) qs.set("fromFy", fromFy);
    if (toFy) qs.set("toFy", toFy);
    const suffix = qs.toString() ? `?${qs}` : "";
    download(`/api/incomes/analytics.xlsx${suffix}`, "salary-by-fy.xlsx").catch((err) =>
      setError(err instanceof ApiError ? err.message : "export failed")
    );
  };

  if (loading && !data) return <p className="muted">Loading year-wise analytics...</p>;
  if (!data) return null;

  const maxGross = Math.max(1, ...data.fys.map((r) => r.gross));

  return (
    <div>
      <div className="toolbar">
        <div className="field">
          <label htmlFor="from-fy">From FY</label>
          <select id="from-fy" value={fromFy} onChange={(e) => setFromFy(e.target.value)}>
            <option value="">Earliest</option>
            {allFys.map((r) => (
              <option key={r.fy} value={r.fy}>{r.fy}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="to-fy">To FY</label>
          <select id="to-fy" value={toFy} onChange={(e) => setToFy(e.target.value)}>
            <option value="">Latest</option>
            {allFys.map((r) => (
              <option key={r.fy} value={r.fy}>{r.fy}</option>
            ))}
          </select>
        </div>
        {(fromFy || toFy) && (
          <button type="button" onClick={() => { setFromFy(""); setToFy(""); }}>Clear</button>
        )}
        <button type="button" onClick={onExport}>Export .xlsx</button>
      </div>

      <div className="list-cards" style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
        <StatCard label={`Total gross (${data.minFy ?? "-"} - ${data.maxFy ?? "-"})`} value={fmtINR(data.totals.gross)} />
        <StatCard label="Total basic" value={fmtINR(data.totals.basic)} />
        <StatCard label="Total net" value={fmtINR(data.totals.net)} />
        <StatCard label="Financial years" value={String(data.count)} />
      </div>

      {data.fys.length === 0 ? (
        <p className="empty-state">No salary rows recorded yet.</p>
      ) : (
        <>
          <div className="card" style={{ marginBottom: "1rem" }}>
            <div className="label" style={{ marginBottom: "0.5rem" }}>Gross salary by financial year</div>
            <BarChart rows={data.fys} maxGross={maxGross} onClick={(r) => onDrillFy(r.fyStart)} />
          </div>
          <div className="card" style={{ marginBottom: "1rem" }}>
            <div className="label" style={{ marginBottom: "0.5rem" }}>Net vs gross trend</div>
            <TrendChart rows={data.fys} />
            <div style={{ display: "flex", gap: "1rem", fontSize: "0.8rem", marginTop: "0.4rem" }}>
              <span style={{ color: "var(--accent)" }}>&#9632; Gross</span>
              <span style={{ color: "var(--success)" }}>&#9632; Net</span>
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Financial Year</th>
                  <th className="num">Gross</th>
                  <th className="num">Basic</th>
                  <th className="num">Net</th>
                  <th className="num">Months</th>
                </tr>
              </thead>
              <tbody>
                {data.fys.map((r) => (
                  <tr key={r.fy} style={{ cursor: "pointer" }} onClick={() => onDrillFy(r.fyStart)} title="View months in this FY">
                    <td>{r.fy}</td>
                    <td className="num">{fmtINR(r.gross)}</td>
                    <td className="num">{fmtINR(r.basic)}</td>
                    <td className="num">{fmtINR(r.net)}</td>
                    <td className="num">{r.months}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: "2px solid var(--border)" }}>
                  <td>Total</td>
                  <td className="num">{fmtINR(data.totals.gross)}</td>
                  <td className="num">{fmtINR(data.totals.basic)}</td>
                  <td className="num">{fmtINR(data.totals.net)}</td>
                  <td className="num">{data.totals.months}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function BarChart({ rows, maxGross, onClick }: { rows: FyRow[]; maxGross: number; onClick: (r: FyRow) => void }) {
  const H = 200, PAD = 24, BAR_GAP = 6;
  const barW = Math.max(10, (600 - PAD * 2) / rows.length - BAR_GAP);
  const W = PAD * 2 + rows.length * (barW + BAR_GAP);
  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, height: "auto" }} role="img" aria-label="Gross salary by FY">
        {rows.map((r, i) => {
          const h = (r.gross / maxGross) * (H - PAD * 2);
          const x = PAD + i * (barW + BAR_GAP);
          const y = H - PAD - h;
          return (
            <g key={r.fy} style={{ cursor: "pointer" }} onClick={() => onClick(r)}>
              <title>{`${r.fy}: ${fmtINR(r.gross)}`}</title>
              <rect x={x} y={y} width={barW} height={h} rx={2} fill="var(--accent)" />
              <text x={x + barW / 2} y={H - PAD + 12} fontSize="9" textAnchor="middle" fill="var(--text-muted)">
                {shortFy(r.fy)}
              </text>
            </g>
          );
        })}
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="var(--border)" />
      </svg>
    </div>
  );
}

function TrendChart({ rows }: { rows: FyRow[] }) {
  const H = 180, PAD = 28;
  const W = Math.max(320, PAD * 2 + (rows.length - 1) * 44);
  const maxV = Math.max(1, ...rows.map((r) => Math.max(r.gross, r.net)));
  const xAt = (i: number) => (rows.length <= 1 ? W / 2 : PAD + (i * (W - PAD * 2)) / (rows.length - 1));
  const yAt = (v: number) => H - PAD - (v / maxV) * (H - PAD * 2);
  const line = (sel: (r: FyRow) => number) => rows.map((r, i) => `${xAt(i)},${yAt(sel(r))}`).join(" ");
  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, height: "auto" }} role="img" aria-label="Net vs gross trend">
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="var(--border)" />
        <polyline fill="none" stroke="var(--accent)" strokeWidth="2" points={line((r) => r.gross)} />
        <polyline fill="none" stroke="var(--success)" strokeWidth="2" points={line((r) => r.net)} />
        {rows.map((r, i) => (
          <text key={r.fy} x={xAt(i)} y={H - PAD + 12} fontSize="9" textAnchor="middle" fill="var(--text-muted)">
            {shortFy(r.fy)}
          </text>
        ))}
      </svg>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card stat-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item-wise (lifetime) YTD items grouped, with category filter buttons,
// search, sort-by-amount, honesty caption, and xlsx export.
// ---------------------------------------------------------------------------
function ItemWise({ setError }: { setError: (e: string | null) => void }) {
  const [data, setData] = useState<PayrollYtd | null>(null);
  const [group, setGroup] = useState("");
  const [q, setQ] = useState("");
  const [sortAsc, setSortAsc] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (group) qs.set("group", group);
    if (q.trim()) qs.set("q", q.trim());
    const suffix = qs.toString() ? `?${qs}` : "";
    api
      .get<PayrollYtd>(`/api/payroll-ytd${suffix}`)
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load payroll items"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [group, q]);

  const onExport = () => {
    const qs = new URLSearchParams();
    if (group) qs.set("group", group);
    if (q.trim()) qs.set("q", q.trim());
    const suffix = qs.toString() ? `?${qs}` : "";
    download(`/api/payroll-ytd.xlsx${suffix}`, "payroll-ytd.xlsx").catch((err) =>
      setError(err instanceof ApiError ? err.message : "export failed")
    );
  };

  const sortedGroups = useMemo(() => {
    if (!data) return [];
    return data.groups.map((g) => ({
      ...g,
      items: [...g.items].sort((a, b) => (sortAsc ? a.total - b.total : b.total - a.total)),
    }));
  }, [data, sortAsc]);

  if (loading && !data) return <p className="muted">Loading item-wise totals...</p>;
  if (!data) return null;

  const gt = data.grandTotals;
  const ALL_GROUPS = ["Earning", "LoanReceived", "Deduction", "Perk", "Memo"];

  return (
    <div>
      <div className="list-cards" style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <StatCard label="Gross earnings" value={fmtINR(gt.grossEarnings)} />
        <StatCard label="Net credited to bank" value={fmtINR(gt.netCredited)} />
        <StatCard label="Total deductions" value={fmtINR(gt.totalDeductions)} />
        <StatCard label="Wage-type items" value={String(gt.itemCount)} />
      </div>

      <p className="muted" style={{ fontSize: "0.8rem", marginBottom: "0.75rem" }}>
        {data.caption} Lifetime gross earnings here exceed the sum of the year-wise gross (Year-wise tab)
        because monthly payslips omit lump wage types like PRP, LFA and leave-encashment that this
        annual report captures.
      </p>

      <div className="toolbar">
        <button type="button" className={group === "" ? "primary" : ""} onClick={() => setGroup("")}>All</button>
        {ALL_GROUPS.map((g) => (
          <button key={g} type="button" className={group === g ? "primary" : ""} onClick={() => setGroup(g)}>
            {GROUP_LABELS[g] ?? g}
          </button>
        ))}
        <div className="field">
          <label htmlFor="ytd-q">Search item</label>
          <input id="ytd-q" placeholder="e.g. Basic, PRP, LFA" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button type="button" onClick={() => setSortAsc((s) => !s)}>
          Sort: {sortAsc ? "Amount ↑" : "Amount ↓"}
        </button>
        <button type="button" onClick={onExport}>Export .xlsx</button>
      </div>

      {sortedGroups.length === 0 && <p className="empty-state">No items match these filters.</p>}

      {sortedGroups.map((g) => (
        <div key={g.group} style={{ marginBottom: "1.25rem" }}>
          <h2 style={{ marginBottom: "0.4rem" }}>
            {GROUP_LABELS[g.group] ?? g.group}{" "}
            <span className="badge">{fmtINR(g.total)}</span>
          </h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Sub-category</th>
                  <th className="num">Lifetime total</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((it) => (
                  <tr key={it.code}>
                    <td>{it.code}</td>
                    <td>{it.description}</td>
                    <td className="muted">{it.subCategory ?? "-"}</td>
                    <td className="num">{fmtINR(it.total)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: "2px solid var(--border)" }}>
                  <td colSpan={3}>{GROUP_LABELS[g.group] ?? g.group} subtotal</td>
                  <td className="num">{fmtINR(g.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Monthly editable salary grid (PUT /salary/:month) + payslip upload +
// other-income CRUD (rent / interest / dividend / other). Preserves the
// original manual-entry surface.
// ---------------------------------------------------------------------------
function MonthlyAndOther({ setError }: { setError: (e: string | null) => void }) {
  const [fyStart, setFyStart] = useState(currentFy());
  const [salaryRows, setSalaryRows] = useState<Record<string, SalaryCell>>({});
  const [savingMonth, setSavingMonth] = useState<string | null>(null);
  const [slipByMonth, setSlipByMonth] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<{ title: string; url: string } | null>(null);

  const [others, setOthers] = useState<Income[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Income | null>(null);
  const [form, setForm] = useState({
    kind: "rent" as (typeof NON_SALARY_KINDS)[number],
    periodMonth: "",
    employerOrSource: "",
    gross: null as number | null,
    net: null as number | null,
    notes: "",
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Upload flow.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedUpload | null>(null);
  const [busy, setBusy] = useState(false);

  const loadSalary = () => {
    api
      .get<Income[]>("/api/incomes?kind=salary")
      .then((rows) => {
        const byMonth: Record<string, SalaryCell> = {};
        for (const key of fyMonths(fyStart).map((m) => m.key)) {
          byMonth[key] = { employer: "", gross: null, basic: null, net: null, saved: false };
        }
        for (const r of rows) {
          byMonth[r.periodMonth] = {
            employer: r.employerOrSource ?? "",
            gross: r.gross,
            basic: r.basic,
            net: r.net,
            saved: true,
          };
        }
        setSalaryRows(byMonth);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load salary"));
  };

  // Map month -> a salary_slip document id, by parsing the FY-month out of the
  // document title (uploads are titled with the source filename, e.g.
  // "Payslip_2024-08.pdf"), so each month can link to its slip when present.
  const loadSlips = () => {
    api
      .get<DocRow[]>("/api/documents?doc_type=salary_slip&limit=200")
      .then((docs) => {
        const map: Record<string, string> = {};
        for (const d of docs) {
          const m = /(\d{4})[-_](\d{1,2})/.exec(d.title);
          if (m) {
            const key = `${m[1]}-${String(Number(m[2])).padStart(2, "0")}-01`;
            if (!map[key]) map[key] = d.id;
          }
        }
        setSlipByMonth(map);
      })
      .catch(() => setSlipByMonth({}));
  };

  const loadOthers = () => {
    Promise.all(NON_SALARY_KINDS.map((k) => api.get<Income[]>(`/api/incomes?kind=${k}`)))
      .then((results) => setOthers(results.flat().sort((a, b) => b.periodMonth.localeCompare(a.periodMonth))))
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load incomes"));
  };

  useEffect(loadSalary, [fyStart]);
  useEffect(loadSlips, []);
  useEffect(loadOthers, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  const updateCell = (month: string, patch: Partial<SalaryCell>) => {
    setSalaryRows((prev) => ({ ...prev, [month]: { ...prev[month], ...patch } }));
  };

  const saveCell = async (month: string) => {
    const cell = salaryRows[month];
    setSavingMonth(month);
    try {
      await api.put(`/api/incomes/salary/${month}`, {
        employer: cell.employer || null,
        gross: cell.gross,
        basic: cell.basic,
        net: cell.net,
      });
      updateCell(month, { saved: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to save salary row");
    } finally {
      setSavingMonth(null);
    }
  };

  const openSlip = async (docId: string, title: string) => {
    try {
      const { url } = await fetchBlobUrl(`/api/documents/${docId}/file`);
      setPreview({ title, url });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not open payslip");
    }
  };

  // --- upload: step 1 preview (confirm=false), step 2 save (confirm=true) ---
  const doUpload = async (confirm: boolean) => {
    if (!uploadFile) return null;
    const fd = new FormData();
    fd.append("file", uploadFile);
    if (confirm) fd.append("confirm", "true");
    return request<ParsedUpload>("/api/incomes/upload", { method: "POST", body: fd });
  };

  const onPickAndPreview = async (e: FormEvent) => {
    e.preventDefault();
    if (!uploadFile) { setError("choose a payslip PDF"); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await doUpload(false);
      setParsed(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onConfirmSave = async () => {
    setBusy(true);
    try {
      await doUpload(true);
      setUploadOpen(false);
      setUploadFile(null);
      setParsed(null);
      loadSalary();
      loadSlips();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "save failed");
    } finally {
      setBusy(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ kind: "rent", periodMonth: "", employerOrSource: "", gross: null, net: null, notes: "" });
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (i: Income) => {
    setEditing(i);
    setForm({
      kind: i.kind as (typeof NON_SALARY_KINDS)[number],
      periodMonth: i.periodMonth,
      employerOrSource: i.employerOrSource ?? "",
      gross: i.gross,
      net: i.net,
      notes: i.notes ?? "",
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const body = {
      kind: form.kind,
      periodMonth: form.periodMonth,
      employerOrSource: form.employerOrSource || null,
      gross: form.gross,
      net: form.net,
      notes: form.notes || null,
    };
    try {
      if (editing) await api.patch(`/api/incomes/${editing.id}`, body);
      else await api.post("/api/incomes", body);
      setModalOpen(false);
      loadOthers();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "save failed");
    }
  };

  const onDelete = async (i: Income) => {
    if (!confirm("Delete this income row?")) return;
    try {
      await api.delete(`/api/incomes/${i.id}`);
      loadOthers();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  const otherColumns: Column<Income>[] = [
    { key: "periodMonth", header: "Period", render: (i) => fmtDate(i.periodMonth) },
    { key: "kind", header: "Kind" },
    { key: "source", header: "Source", render: (i) => i.employerOrSource ?? "-" },
    { key: "gross", header: "Gross", align: "right", render: (i) => fmtINR(i.gross) },
    { key: "net", header: "Net", align: "right", render: (i) => fmtINR(i.net) },
    {
      key: "actions",
      header: "",
      render: (i) => (
        <span className="actions-inline">
          <button type="button" onClick={() => openEdit(i)}>Edit</button>
          <button type="button" className="danger" onClick={() => onDelete(i)}>Delete</button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <h2>Monthly salary</h2>
      <div className="toolbar">
        <button type="button" onClick={() => setFyStart((y) => y - 1)}>
          &laquo; FY {fyStart - 1}-{String(fyStart).slice(2)}
        </button>
        <strong>FY {fyStart}-{String(fyStart + 1).slice(2)}</strong>
        <button type="button" onClick={() => setFyStart((y) => y + 1)}>
          FY {fyStart + 1}-{String(fyStart + 2).slice(2)} &raquo;
        </button>
        <button className="primary" type="button" onClick={() => { setUploadOpen(true); setParsed(null); setUploadFile(null); }}>
          Upload payslip
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: preview ? "1fr 1fr" : "1fr", gap: "1rem" }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Employer</th>
                <th className="num">Gross</th>
                <th className="num">Basic</th>
                <th className="num">Net</th>
                <th>Slip</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {fyMonths(fyStart).map(({ key, label }) => {
                const cell = salaryRows[key] ?? { employer: "", gross: null, basic: null, net: null, saved: false };
                const slip = slipByMonth[key];
                return (
                  <tr key={key}>
                    <td>{label}</td>
                    <td><input value={cell.employer} onChange={(e) => updateCell(key, { employer: e.target.value })} /></td>
                    <td className="num"><MoneyInput value={cell.gross} onChange={(v) => updateCell(key, { gross: v })} /></td>
                    <td className="num"><MoneyInput value={cell.basic} onChange={(v) => updateCell(key, { basic: v })} /></td>
                    <td className="num"><MoneyInput value={cell.net} onChange={(v) => updateCell(key, { net: v })} /></td>
                    <td>
                      {slip ? (
                        <button type="button" onClick={() => openSlip(slip, `${label} payslip`)}>View</button>
                      ) : (
                        <span className="muted">-</span>
                      )}
                    </td>
                    <td>
                      <button type="button" disabled={savingMonth === key} onClick={() => saveCell(key)}>
                        {savingMonth === key ? "Saving..." : cell.saved ? "Update" : "Save"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {preview && (
          <div className="card">
            <div className="modal-header">
              <strong>{preview.title}</strong>
              <button type="button" onClick={() => setPreview(null)}>Close</button>
            </div>
            <iframe title="payslip" src={preview.url} style={{ width: "100%", height: "70vh", border: "none" }} />
          </div>
        )}
      </div>

      <h2>Other income (rent / interest / dividend / other)</h2>
      <div className="toolbar">
        <button className="primary" type="button" onClick={openAdd}>Add income</button>
      </div>
      <Table columns={otherColumns} rows={others} rowKey={(i) => i.id} emptyText="No other income recorded yet." />

      {uploadOpen && (
        <Modal title="Upload payslip" onClose={() => setUploadOpen(false)}>
          {!parsed ? (
            <form onSubmit={onPickAndPreview} className="modal-body">
              <div className="field">
                <label htmlFor="slip-file">Payslip PDF</label>
                <input id="slip-file" type="file" accept="application/pdf,.pdf" onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)} />
              </div>
              <p className="muted" style={{ fontSize: "0.8rem" }}>
                The PDF is stored in your vault and we try to read the month and amounts. You confirm before anything is saved.
              </p>
              <div className="modal-actions">
                <button type="button" onClick={() => setUploadOpen(false)}>Cancel</button>
                <button className="primary" type="submit" disabled={busy || !uploadFile}>
                  {busy ? "Reading..." : "Read payslip"}
                </button>
              </div>
            </form>
          ) : (
            <div className="modal-body">
              <p className="muted" style={{ fontSize: "0.85rem" }}>
                Stored in vault. Review the parsed values (blank means not detected). Saving fills only blank fields for that
                month; it never overwrites a value you already entered.
              </p>
              <table>
                <tbody>
                  <tr><td>Period</td><td className="num">{parsed.periodMonth ? fmtDate(parsed.periodMonth) : "not detected"}</td></tr>
                  <tr><td>Gross</td><td className="num">{parsed.gross != null ? fmtINR(parsed.gross) : "-"}</td></tr>
                  <tr><td>Basic</td><td className="num">{parsed.basic != null ? fmtINR(parsed.basic) : "-"}</td></tr>
                  <tr><td>Net</td><td className="num">{parsed.net != null ? fmtINR(parsed.net) : "-"}</td></tr>
                </tbody>
              </table>
              {parsed.matched && <p className="muted">A salary row already exists for this month; existing values are kept.</p>}
              <div className="modal-actions">
                <button type="button" onClick={() => { setParsed(null); }}>Back</button>
                <button className="primary" type="button" disabled={busy || !parsed.periodMonth} onClick={onConfirmSave}>
                  {busy ? "Saving..." : "Confirm & save"}
                </button>
              </div>
              {!parsed.periodMonth && <p className="error-text">No month detected — the PDF is saved to your vault, but there is nothing to post to the salary grid.</p>}
            </div>
          )}
        </Modal>
      )}

      {modalOpen && (
        <Modal title={editing ? "Edit income" : "Add income"} onClose={() => setModalOpen(false)}>
          <form onSubmit={onSubmit} className="modal-body">
            <div className="form-grid">
              <div className="field">
                <label htmlFor="in-kind">Kind</label>
                <select id="in-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as (typeof NON_SALARY_KINDS)[number] })}>
                  {NON_SALARY_KINDS.map((k) => (<option key={k} value={k}>{k}</option>))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="in-period">Period (month)</label>
                <DateInput value={form.periodMonth} onChange={(v) => setForm({ ...form, periodMonth: v.slice(0, 8) + "01" })} required />
              </div>
              <div className="field">
                <label htmlFor="in-source">Source</label>
                <input id="in-source" value={form.employerOrSource} onChange={(e) => setForm({ ...form, employerOrSource: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="in-gross">Gross</label>
                <MoneyInput value={form.gross} onChange={(v) => setForm({ ...form, gross: v })} />
              </div>
              <div className="field">
                <label htmlFor="in-net">Net</label>
                <MoneyInput value={form.net} onChange={(v) => setForm({ ...form, net: v })} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="in-notes">Notes</label>
              <textarea id="in-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {formError && <div className="error-text">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="primary" type="submit">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
